import { put, takeLatest } from 'redux-saga/effects';

import { ApiResult } from 'common/models';
import { PayloadAction } from '@reduxjs/toolkit';

import { MessageType } from 'common/enums';
import { appStateActions } from 'common/redux/app-state';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { FAILURE_HEADER } from 'common/config';
import {
    COMPANY_INFO_EDIT_FAIL,
    COMPANY_INFO_EDIT_SUCCESS,
    COMPANY_FOOTPRINT_UPDATE_FAIL,
    SUCCESS_HEADER
} from 'common/config/message.config';

import { DataManagementService } from './data-management.service';
import { dataManagementActions } from './data-management.slice';
import { CompanyInfoDTO, LocationDetails } from '../models/data-management.model';

const fetchCompanyFootprints = function* ({ payload }: PayloadAction<PagedRequest>) {
    const { hasErrors, errors, value } = yield DataManagementService.getCompanyFootprints(payload);
    if (hasErrors && errors) {
        yield put({
            type: dataManagementActions.fetchCompanyFootprintFailed.type,
            payload: value?.detail
        });
    } else {
        yield put({
            type: dataManagementActions.fetchCompanyFootprintSuccess.type,
            payload: value
        });
    }
};

const fetchCompanyFootprintsWatch = function* () {
    yield takeLatest(dataManagementActions.fetchCompanyFootprint.type, fetchCompanyFootprints);
};

const updateLocationWiseCompanyFootprint = function* ({ payload }: PayloadAction<LocationDetails>) {
    const { hasErrors, errors, value }: ApiResult<boolean> =
        yield DataManagementService.updateLocationWiseCompanyFootprint(payload);
    if (hasErrors && errors) {
        yield put({
            type: dataManagementActions.updateCompanyFootprintFailed.type,
            payload: errors
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: COMPANY_FOOTPRINT_UPDATE_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        if (value) {
            yield put({
                type: dataManagementActions.updateCompanyFootprintSuccess.type,
                payload: payload
            });
        } else {
            yield put({
                type: dataManagementActions.updateCompanyFootprintFailed.type,
                payload: value
            });
            yield put({
                type: appStateActions.showMessageModal.type,
                payload: {
                    isVisible: true,
                    header: FAILURE_HEADER,
                    message: COMPANY_FOOTPRINT_UPDATE_FAIL,
                    type: MessageType.Error
                }
            });
        }
    }
};

const updateLocationWiseCompanyFootprintWatch = function* () {
    yield takeLatest(
        dataManagementActions.updateCompanyFootprint.type,
        updateLocationWiseCompanyFootprint
    );
};

const fetchCompanyInfo = function* () {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const companyInfoRep: ApiResult<any> = yield DataManagementService.getCompanyInfo();
    if (companyInfoRep?.errors && companyInfoRep?.hasErrors) {
        yield put({
            type: dataManagementActions.fetchCompanyInfoFailed.type,
            payload: companyInfoRep?.errors
        });
    } else {
        yield put({
            type: dataManagementActions.fetchCompanyInfoSuccess.type,
            payload: companyInfoRep?.value
        });
    }
};

const fetchCompanyInfoWatch = function* () {
    yield takeLatest(dataManagementActions.fetchCompanyInfo.type, fetchCompanyInfo);
};

const updateCompanyInfo = function* ({ payload }: PayloadAction<CompanyInfoDTO>) {
    const { hasErrors, errors, value }: ApiResult<boolean> =
        yield DataManagementService.updateCompanyInfo(payload);
    if (hasErrors && errors) {
        yield put({
            type: dataManagementActions.updateCompanyInfoFailed.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: COMPANY_INFO_EDIT_FAIL,
                type: MessageType.Error
            }
        });
    } else {
        yield put({
            type: dataManagementActions.updateCompanyInfoSuccess.type,
            payload: payload
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: COMPANY_INFO_EDIT_SUCCESS,
                type: MessageType.Success
            }
        });
    }
};

const updateCompanyInfoWatch = function* () {
    yield takeLatest(dataManagementActions.updateCompanyInfo.type, updateCompanyInfo);
};

export {
    fetchCompanyFootprintsWatch,
    fetchCompanyInfoWatch,
    updateCompanyInfoWatch,
    updateLocationWiseCompanyFootprintWatch
};
